import React from 'react';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { ChakraProvider } from '@chakra-ui/react';

import ErrorBoundary from './components/ErrorBoundary';
import CreatePasswordPage from './pages/CreatePasswordPage';
import ErrorPage from './pages/ErrorPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import LoginPage from './pages/LoginPage';
import MissingUserPage from './pages/MissingUserPage';
import PinsAdminPage from './pages/PinsAdminPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import store from './redux/middleware';
import InsuranceRequestRoutes from './routes/insuranceRequestRoutes';
import SidePanels from './routes/sidePanels';
import pinsTheme from './styles/theme';
import { bugsnagDiscardedMessages } from './utils/bugsnagUtil';
import { queryClient } from './utils/queryClientUtil';
import { ToastContainer } from './utils/toastUtil';
import MainRoutes from './routes';

import './App.css';

BugsnagPerformance.start({
  apiKey: '54be95e31342c218f6830d669e0aee49',
  appVersion: BUILD_VERSION,
  releaseStage: PINS_ENV
});

Bugsnag.start({
  apiKey: '54be95e31342c218f6830d669e0aee49',
  appVersion: BUILD_VERSION,
  plugins: [new BugsnagPluginReact()],
  releaseStage: PINS_ENV,
  enabledBreadcrumbTypes: ['navigation', 'request', 'error', 'user', 'state', 'manual'],
  logger: {
    debug: message => console.warn(message), // eslint-disable-line no-console
    info: message => console.warn(message), // eslint-disable-line no-console
    warn: message => console.warn(message), // eslint-disable-line no-console
    error: message => console.warn(message) // eslint-disable-line no-console
  },
  onError(event) {
    const errorMessage = event.errors?.[0]?.errorMessage || '';

    if (bugsnagDiscardedMessages.some(m => errorMessage.includes(m))) {
      return false;
    }

    return true;
  }
});

/**
 * Suppress prop-types errors
 */
const errorsToWarn = [
  'Warning:',
  'The above error'
];
const oldConsError = console.error; // eslint-disable-line no-console

console.error = (...args) => { // eslint-disable-line no-console
  let toWarn = false;

  if (typeof args[0] === 'string') {
    errorsToWarn.map(_s => {
      if (args[0].startsWith(_s)) {
        toWarn = true;
      }
    });
  }

  toWarn ? console.warn(...args) : oldConsError(...args); // eslint-disable-line no-console
};

const BugsnagErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

const App = () => (
  <div className="App">
    <ChakraProvider theme={pinsTheme}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <BugsnagErrorBoundary FallbackComponent={ErrorPage}>
              <Router basename="v2">
                <Route
                  component={LoginPage}
                  path="/login/"
                />
                <Route
                  component={MissingUserPage}
                  path="/missing-user/"
                />
                <Route
                  component={ForgotPasswordPage}
                  path="/forgot-password/"
                />
                <Route
                  component={PinsAdminPage}
                  path="/pinsadmin"
                />
                <Route
                  component={CreatePasswordPage}
                  path="/create-password/users/:userEmail/key/:passwordKey"
                />
                <Route
                  component={ResetPasswordPage}
                  path="/password-reset/email/:userEmail/key/:passwordKey"
                />
                <Route
                  component={InsuranceRequestRoutes}
                  path="/insurance-requests/:insuranceRequestId"
                />
                <Route
                  component={MainRoutes}
                  path="/companies/:companyId"
                />
                <Route
                  component={SidePanels}
                  path="/side-panels"
                />
              </Router>
            </BugsnagErrorBoundary>
          </ErrorBoundary>
          {PINS_ENV !== 'prod' && (
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          )}
        </QueryClientProvider>
      </Provider>
      <ToastContainer />
    </ChakraProvider>
  </div>
);

export default App;
